/**
 * The on-device segmenter's lifetime, owned by a module instead of by a component's unmount.
 *
 * AUD-2 IM-2 / F-35: `MaskEditor`'s cleanup nulled the loaded-model cache and called
 * `engine.releaseSegmentationModel()`, and `TemplateEditor` keyed the editor on `activeView` — so
 * a view-tab switch, a Force sync, a view removal and the auto-`openDraft` on mount each evicted a
 * model the code itself budgets at "the first run may download ~109 MB", then reloaded it ~12 s
 * later. The original cleanup was right about the GOAL (the model + ORT arena held GBs, and the
 * user reported "6.5 GB still held") and wrong about the SCOPE: the editor CLOSING is the event
 * that should free it, not the editor RE-RENDERING.
 *
 * So: a refcount plus a grace window. A remount inside the window — every one of F-35's four
 * triggers — cancels the pending release and keeps the model. A genuine close lets the timer run
 * and frees it, preserving the RAM fix this replaces.
 *
 * `releaseSegmentationModel` on the binding is OPTIONAL and feature-detected (`binding.ts:200`);
 * the wasm/web build does not have it and has no AI matte at all (C-12). The `?.` is the platform
 * split, not defensiveness.
 */
import type { EngineBinding } from "@genz/engine-bind";

/** How long the model survives with no editor mounted. Long enough to cover a view-tab switch, a
 *  Force-sync remount and the auto-`openDraft`; short enough that closing the editor still frees
 *  the GBs within one "did it let go yet?" glance. */
export const SEG_MODEL_GRACE_MS = 30_000;

/**
 * The model bytes last handed to the native segmenter (was `MaskEditor`'s module-level
 * `nativeLoadedSegModel`, then `useMatte`'s). Module-scoped so the ~109 MB buffer is re-serialized
 * across the IPC boundary only when it actually changes — the `Array.from`-on-a-109MB-buffer that
 * used to freeze the editor and the progress bar on every removal.
 */
let loadedBytes: Uint8Array | null = null;
/** How many mounted editors are using the segmenter right now. */
let retainCount = 0;
/** The pending grace-window release, if one is scheduled. */
let releaseTimer: ReturnType<typeof setTimeout> | null = null;

/** The model bytes the native segmenter currently holds, or null. */
export function loadedSegmentationModel(): Uint8Array | null {
  return loadedBytes;
}
/** Record which bytes the native segmenter now holds. */
export function setLoadedSegmentationModel(bytes: Uint8Array | null): void {
  loadedBytes = bytes;
}

/**
 * One mounted editor is using the segmenter.
 *
 * Cancelling a pending release here is the whole fix: a remount (view switch, Force sync, prune,
 * `openDraft`) lands inside the grace window and the model is never touched.
 */
export function retainSegmentationModel(): void {
  retainCount += 1;
  if (releaseTimer !== null) {
    clearTimeout(releaseTimer);
    releaseTimer = null;
  }
}

/**
 * One mounted editor is done with the segmenter. At refcount 0 the release is SCHEDULED, not
 * performed — see `SEG_MODEL_GRACE_MS`. Above 0 (two editors open) nothing is scheduled at all.
 */
export function releaseSegmentationModel(
  engine: Pick<EngineBinding, "releaseSegmentationModel">,
): void {
  retainCount = Math.max(0, retainCount - 1);
  if (retainCount > 0) return;
  if (releaseTimer !== null) clearTimeout(releaseTimer);
  releaseTimer = setTimeout(() => {
    releaseTimer = null;
    // A retain may have landed between the timer firing and this callback in a fake-timer test;
    // re-check rather than evicting a model an editor is using.
    if (retainCount > 0) return;
    void releaseSegmentationModelNow(engine);
  }, SEG_MODEL_GRACE_MS);
}

/**
 * Free the native session NOW, ignoring the grace window — the editor route unmounting, or a test.
 * Clears the cached bytes too, so the next removal re-hands them across the boundary.
 */
export async function releaseSegmentationModelNow(
  engine: Pick<EngineBinding, "releaseSegmentationModel">,
): Promise<void> {
  if (releaseTimer !== null) {
    clearTimeout(releaseTimer);
    releaseTimer = null;
  }
  loadedBytes = null;
  // Optional on the binding: absent on wasm/web, which has no AI matte at all (C-12).
  await engine.releaseSegmentationModel?.();
}

/** Test-only: reset the refcount, the timer and the cached bytes. */
export function __resetSegmentationModelForTest(): void {
  if (releaseTimer !== null) clearTimeout(releaseTimer);
  releaseTimer = null;
  retainCount = 0;
  loadedBytes = null;
}
